export const errorsToIgnore = [
  // Ignored as part of legacy page that causes weird errors https://app.shortcut.com/ifttt/story/47191
  "YsAppBridge is not defined",

  // Mate Translate browser extension error
  "MtPopUpList is not defined",

  // Difficult-to-catch error from Google Geocoder API
  "GEOCODER_GEOCODE: ZERO_RESULTS",

  // Firefox/Safari private mode with no known consequences https://app.shortcut.com/ifttt/story/126427
  "No identifiers allowed directly after numeric literal",

  // Yandex error mostly from no-longer-supported 1Password in-app browser
  "window.yandexReadabilityChecker.isProbablyReaderable",

  // Telegram webview error from Headless Chrome (i.e. likely not an actual user)
  "window.TelegramWebview.post is not a function",

  // This is an error from Matomo ("a privacy-friendly Google Analytics alternative") outside of our control
  // https://developer.matomo.org/guides/form-analytics/reference/
  "Java bridge method invocation error",

  // This is a mysterious error that Textfit (used by Fittty) encounters with little or no user
  // experience ramifications. Our use of Textfit is now wrapped in an ErrorBoundary as well.
  // See https://app.shortcut.com/ifttt/story/126426 / https://github.com/IFTTT/web/pull/8562
  "Argument 1 ('element') to Window.getComputedStyle must be an instance of Element",
  "Window.getComputedStyle: Argument 1 is not an object",
  "Failed to execute 'getComputedStyle' on 'Window': parameter 1 is not of type 'Element'",

  // .NET embedded Chromium tool (CefSharp) used to crawl sites.
  // Details: https://trackjs.com/javascript-errors/object-not-found-matching-id-methodname-paramcount/
  "Object Not Found Matching Id",

  // This is expected to prevent Google Sign in iFrame from showing to keep all buttons consistent. See ApplicationHelper#block_google_signin_iframe
  "csp_violation: 'https://accounts.google.com' blocked by 'frame-src' directive",
  "csp_violation: 'https://accounts.google.com/' blocked by 'frame-src' directive",

  // We're using an old verion of Monaco editor. During unmount, few internal components aren't disposed properly.
  // It doesn't impact users directly. It was fixed on version 4.2+.
  // Issues related: https://github.com/microsoft/monaco-editor/issues/1638 and https://github.com/microsoft/monaco-editor/issues/1842
  "Could not find file: 'inmemory://model/1'",

  // An old WebKit bug results in these generic errors being logged to Datadog by Safari and other WebKit-based browsers even when CORS headers are configured correctly. The errors are not useful or actionable in this state.
  // https://bugs.webkit.org/show_bug.cgi?id=132945
  // https://blog.sentry.io/what-is-script-error
  "Script error.",

  // We have been able to reproduce the error locally, however it only appears in RUM and not in the local browser's developer tools, which makes it virtually impossible to troubleshoot. There is no evidence that the error caused any UX or performance issues, so it's more reasonable to ignore it at this point.
  "Uncaught \"Empty reason\"",

  // "grecaptcha is not defined" was filtered here as bot noise. It wasn't: it was real users whose
  // signup silently died when reCAPTCHA couldn't load, hidden for as long as this entry existed.
  // Left visible on purpose now that shared/handle_recaptcha.js submits without a token instead.
  "Uncaught \"Timeout\"",
]

// Chrome iOS and the Google app inject a page-translation script that overflows the stack.
// Its frames carry the page URL, never our asset host, so an overflow with one of our frames is still ours.
export function isThirdPartyStackOverflow(event) {
  const exception = event?.exception?.values?.[0]
  if (!exception?.value?.includes("Maximum call stack size exceeded")) return false

  const frames = exception.stacktrace?.frames || []
  return !frames.some(frame => frame?.filename?.includes("web-assets"))
}

// We only serve English, so a class or lang that browser translation sets means the page is translated.
export function isPageTranslated(doc = document) {
  const html = doc?.documentElement
  if (!html) return false

  const translatedClass = html.classList.contains("translated-ltr") || html.classList.contains("translated-rtl")
  return translatedClass || !(html.lang || "en").toLowerCase().startsWith("en")
}

// Keep these overflows visible as one warning issue instead of one error issue per page.
export function groupThirdPartyStackOverflow(event, doc = document) {
  if (!isThirdPartyStackOverflow(event)) return event

  return {
    ...event,
    level: "warning",
    fingerprint: ["third-party-stack-overflow"],
    tags: {
      ...event.tags,
      third_party_stack_overflow: "true",
      page_translated: String(isPageTranslated(doc)),
      page_lang: doc?.documentElement?.lang || "",
    },
  }
}
